import type { NotificationPreferences } from '@central/api';
import { Alert, Loader, Stack, Switch } from '@mantine/core';
import { Card } from './Card';
import { errorMessage } from './errors';
import { notificationCategories, type NotificationCategory } from './notifications';
import { color } from './tokens';

export interface NotificationPreferencesCardProps {
  /** Absent while loading. */
  prefs?: NotificationPreferences;
  loadError?: unknown;
  /** The category being saved, whose switch shows as busy. */
  saving?: NotificationCategory;
  saveError?: unknown;
  onChange: (key: NotificationCategory, on: boolean) => void;
}

/**
 * The signed-in user's own email notification switches. Each change is
 * saved at once; account emails (sign-in links, password resets, invites)
 * are not a category and are always sent.
 */
export function NotificationPreferencesCard({
  prefs,
  loadError,
  saving,
  saveError,
  onChange,
}: NotificationPreferencesCardProps) {
  return (
    <Card title="Email notifications">
      <p style={{ margin: '0 0 14px', fontSize: 14, color: color.textMuted }}>
        Choose which emails you receive. Sign-in links, password resets and invites are always sent.
      </p>
      {loadError ? (
        <Alert color="red">{errorMessage(loadError)}</Alert>
      ) : !prefs ? (
        <Loader size="sm" aria-label="Loading notification settings" />
      ) : (
        <Stack gap={14}>
          {notificationCategories.map((c) => (
            <Switch
              key={c.key}
              label={c.label}
              description={c.description}
              checked={prefs[c.key]}
              disabled={saving !== undefined}
              onChange={(e) => onChange(c.key, e.currentTarget.checked)}
            />
          ))}
          {saveError !== undefined && saveError !== null && (
            <Alert color="red">{errorMessage(saveError)}</Alert>
          )}
        </Stack>
      )}
    </Card>
  );
}
